import { fetchApi } from '@/services/api-client';

export const BOOKINGS_PATH = '/bookings';

export type BookingResourceType = 'room' | 'tablet';

export type BookableRoom = { id: string; name: string; code: string | null };

export type TabletPool = { totalCount: number };

export type BookingUser = { id: string; displayName: string };
export type BookingRoom = { id: string; name: string };
export type BookingClass = { id: string; name: string; gradeLevel: number };

export type Booking = {
  id: string;
  resourceType: BookingResourceType;
  date: string;
  periodId: string;
  /** Resolved against the date's bell schedule, so a short day reports its own times. */
  periodName: string | null;
  startTime: string | null;
  endTime: string | null;
  roomId: string;
  room: BookingRoom | null;
  classId: string | null;
  schoolClass: BookingClass | null;
  quantity: number | null;
  bookedByUserId: string;
  bookedByUser: BookingUser | null;
  note: string | null;
  createdAt: string;
};

export type RoomAvailabilityReason = 'scheduled' | 'booked';

export type RoomAvailability = {
  roomId: string;
  roomName: string;
  free: boolean;
  reason?: RoomAvailabilityReason;
  /** Class the timetable puts in this room then — set when `reason` is 'scheduled'. */
  className?: string;
  /** Who holds the booking — set when `reason` is 'booked'. */
  bookedByName?: string;
};

/** One period's row of the booking grid. */
export type BookingDayPeriod = {
  periodId: string;
  periodName: string;
  startTime: string;
  endTime: string;
  rooms: RoomAvailability[];
  tablets: { totalCount: number; booked: number; remaining: number };
};

/** Every bookable period of one date, with each room's status. */
export type BookingDayAvailability = {
  date: string;
  periods: BookingDayPeriod[];
};

/** A booking as the dashboard widget shows it. */
export type UpcomingBooking = {
  id: string;
  resourceType: BookingResourceType;
  date: string;
  periodId: string;
  periodName: string;
  startTime: string;
  endTime: string;
  roomName: string | null;
  quantity: number | null;
  className: string | null;
  bookedByName: string | null;
};

/** Limits the booking form respects before a date is chosen. */
export type BookingLimits = {
  maxAdvanceDays: number;
  /** Latest bookable date (YYYY-MM-DD), or null when no horizon is configured. */
  maxDate: string | null;
};

export type BookingAvailability = {
  date: string;
  periodId: string;
  rooms: RoomAvailability[];
  tablets: { totalCount: number; booked: number; remaining: number };
};

export type ActiveSchoolYear = { id: string; name: string };

export type CreateBookingData = {
  resourceType: BookingResourceType;
  date: string;
  periodId: string;
  roomId: string;
  classId?: string;
  quantity?: number;
  note?: string;
};

export type BookingListFilters = {
  from?: string;
  to?: string;
  resourceType?: BookingResourceType;
  roomId?: string;
  classId?: string;
  bookedByUserId?: string;
};

function toQuery(filters: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '') qs.append(key, value);
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const bookingsApi = {
  bookableRooms: () => fetchApi<BookableRoom[]>(`${BOOKINGS_PATH}/rooms`),
  activeSchoolYear: () => fetchApi<ActiveSchoolYear>(`${BOOKINGS_PATH}/active-school-year`),
  tabletPool: () => fetchApi<TabletPool>(`${BOOKINGS_PATH}/tablet-pool`),
  setTabletPool: (totalCount: number) =>
    fetchApi<TabletPool>(`${BOOKINGS_PATH}/tablet-pool`, { method: 'PUT', body: JSON.stringify({ totalCount }) }),
  availability: (date: string, periodId: string) =>
    fetchApi<BookingAvailability>(`${BOOKINGS_PATH}/availability${toQuery({ date, periodId })}`),

  dayAvailability: (date: string) =>
    fetchApi<BookingDayAvailability>(`${BOOKINGS_PATH}/availability/day${toQuery({ date })}`),

  limits: () => fetchApi<BookingLimits>(`${BOOKINGS_PATH}/limits`),

  upcoming: (limit = 5) => fetchApi<UpcomingBooking[]>(`${BOOKINGS_PATH}/upcoming${toQuery({ limit: String(limit) })}`),
  create: (data: CreateBookingData) => fetchApi<Booking>(BOOKINGS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  listMine: () => fetchApi<Booking[]>(`${BOOKINGS_PATH}/mine`),
  listAll: (filters: BookingListFilters = {}) => fetchApi<Booking[]>(`${BOOKINGS_PATH}${toQuery(filters)}`),
  cancel: (id: string) => fetchApi<{ ok: true }>(`${BOOKINGS_PATH}/${id}`, { method: 'DELETE' }),
  detail: (id: string) => fetchApi<Booking>(`${BOOKINGS_PATH}/${id}`),
};
